import { PressResponder } from '@react-aria/interactions';
import { Children, isValidElement } from 'react';
import {
  DialogTrigger as AriaDialogTrigger,
  type DialogTriggerProps as AriaDialogTriggerProps,
} from 'react-aria-components';

import { DialogMarshal } from './DialogMarshal';
import { useDialogState } from './useDialogState';

export type DialogTriggerProps = AriaDialogTriggerProps;

/**
 * A controller for dialog state, bound to an associated trigger; linking the
 * `Dialog` open state with the trigger’s press state.
 */
export function DialogTrigger(props: DialogTriggerProps) {
  const { children, ...otherProps } = props;

  return (
    <AriaDialogTrigger {...otherProps}>
      <DialogElements>{children}</DialogElements>
    </AriaDialogTrigger>
  );
}

/**
 * Repurpose the `DialogMarshal`, specifically its implementation of conditional
 * rendering to mount/unmount dialogs properly.
 */
function DialogElements(props: DialogTriggerProps) {
  const { children, ...otherProps } = props;
  const [trigger, dialog] = Children.toArray(children);
  const state = useDialogState();

  return (
    <>
      {/*
        RAC sets "isPressed" via `PressResponder` when the dialog is open.
        We don't want the press state to appear to get "stuck", so override this.
      */}
      <PressResponder isPressed={false}>{trigger}</PressResponder>
      <DialogMarshal onDismiss={state.close} {...otherProps}>
        {state.isOpen && isValidElement(dialog) ? dialog : null}
      </DialogMarshal>
    </>
  );
}
